import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CWTSchemaField, FieldType } from '../../../../../types/custom-worksheet-v2';
import { formatFieldValueToDisplay } from '../../../../worksheet-v2/custom-worksheet-v2/utils';
import { editField } from '../../../store/slice';
import { Description } from '../../side-panel/Configurations';
import CWFieldInput from '../../../../../components/custom-worksheet/CWFieldInput';

type FieldDefaultValueProps = {
    field: CWTSchemaField;
};

function FieldDefaultValue({ field }: FieldDefaultValueProps): JSX.Element {
    const dispatch = useDispatch();
    const [value, setValue] = useState(field.defaultValue);

    const outerDivRef = useRef<HTMLDivElement | null>(null);

    const handleSave = useCallback(
        function handleSave(): void {
            let _value = value;

            if (_value === '' || _value === null) {
                _value = null;
            }

            const numberTypes = [FieldType.NUMBER, FieldType.PERCENT, FieldType.ACCOUNTING, FieldType.MONEY];

            if (numberTypes.includes(field.type) && (_value === '.' || _value === '-')) {
                return;
            }

            // If value entered is greater than the MAX_SAFE_INTEGER, don't save the value
            if (typeof _value === 'number' && _value > Number.MAX_SAFE_INTEGER) {
                return;
            }

            // If default value hasn't change, don't update
            if (_value === field.defaultValue) {
                return;
            }

            dispatch(editField({ id: field.id, details: { defaultValue: _value } }));
        },
        [dispatch, field, value]
    );

    /**
     * When user clicks outside of the input area, blurs the input and save updated value
     */
    const handleClickOutside = useCallback(
        function handleClickOutside(event: MouseEvent): void {
            if (!(event.target instanceof Element)) {
                return;
            }

            // If click event is within outerDivRef area, don't clear edit layout
            if (outerDivRef?.current && (outerDivRef.current.contains(event.target) || outerDivRef.current.isEqualNode(event.target))) {
                return;
            }

            // If click event is out of outerDivRef area
            if (outerDivRef?.current && !outerDivRef.current.contains(event.target)) {
                handleSave();
            }
        },
        [handleSave]
    );

    /**
     * Revert changes on input value on 'Escape' key clicked
     * Save changes on input value on 'Enter' key clicked
     */
    const handleKeyboardShortcut = useCallback(
        function handleKeyboardShortcut(event: KeyboardEvent): void {
            if (event.key === 'Escape') {
                setValue(formatFieldValueToDisplay(field.type, field.defaultValue, true));
            } else if (event.key === 'Enter') {
                handleSave();
            }
        },
        [handleSave, field.type, field.defaultValue]
    );

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return (): void => document.removeEventListener('mousedown', handleClickOutside);
    }, [handleClickOutside]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyboardShortcut);
        return (): void => document.removeEventListener('keydown', handleKeyboardShortcut);
    }, [handleKeyboardShortcut]);

    useEffect(() => {
        setValue(field.defaultValue);
    }, [field.id, field.defaultValue]);

    return (
        <div className="flex flex-col gap-1">
            <Description
                title="Default value"
                description="The value to be filled-in by default. This value can be replaced in the worksheet."
                isOptional
            />
            <div ref={outerDivRef}>
                <CWFieldInput
                    autoFocus={false}
                    fieldType={field.type}
                    onValueChange={setValue}
                    defaultValue={field.defaultValue}
                    showIcon
                />
            </div>
        </div>
    );
}

export default FieldDefaultValue;
